import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppIcon, AppText, ReactSelectComponents, Select, styled, Tooltip, UIConstants, useTheme, } from '@streem/ui-react';
import { getPreferredVideoInputId } from '@streem/toolbox';
import { TooltipMessage } from './toolbar_tooltip_message';
import { useObservable } from '../../../../hooks/rxjs';
import { useRoomSession } from '../../../../context/room_session';
import { useLocalVideoControllerState } from '../../../../hooks/use_local_video_controller_state';
import { logger } from '../../../../util/logger';
const log = logger.extend('InCallLocalVideoInputSelect');
export const LocalVideoInputSelect = () => {
    const theme = useTheme();
    const roomSession = useRoomSession();
    const [localVideoLabel] = useObservable(roomSession.media.localVideoLabel);
    const [localVideoController] = useObservable(roomSession.media.localVideoController);
    const [localVideoControllerState] = useLocalVideoControllerState(roomSession.media.localVideoController);
    const [localDevices] = useObservable(roomSession.media.localDevices);
    const [cameraSelectValue, setCameraSelectValue] = useState('');
    const [showTooltip, setShowTooltip] = useState(false);
    const localCameraDevices = useMemo(() => {
        if (Array.isArray(localDevices) && localDevices.length > 0) {
            return localDevices.filter(device => device.kind === 'videoinput');
        }
        return [];
    }, [localDevices]);
    const areLocalCameraDevicesAvailable = useMemo(() => {
        return (localCameraDevices.filter(device => device.deviceId || device.deviceId !== '').length >
            0);
    }, [localCameraDevices]);
    useEffect(() => {
        const setupCameraAndDevices = async () => {
            try {
                const preferredCameraId = getPreferredVideoInputId();
                const attachedDevice = localCameraDevices.length > 0
                    ? localCameraDevices.find(device => device.deviceId === preferredCameraId)
                    : undefined;
                if (preferredCameraId && attachedDevice) {
                    setCameraSelectValue(preferredCameraId);
                }
            }
            catch (error) {
                log.error('error setting up camera and devices', error);
            }
        };
        setupCameraAndDevices();
    }, [localCameraDevices]);
    useEffect(() => {
        // if we don't have a preferred camera selected or the device is not attached, check for which one is currently in use to show the checkmark in the select dropdown
        if (localVideoLabel && !cameraSelectValue && localCameraDevices.length > 0) {
            setCameraSelectValue(localCameraDevices.find(device => device.label === localVideoLabel)?.deviceId || '');
        }
    }, [localVideoLabel, cameraSelectValue, localCameraDevices]);
    const show = useCallback(() => {
        if (!areLocalCameraDevicesAvailable) {
            setShowTooltip(true);
        }
    }, [areLocalCameraDevicesAvailable]);
    const hide = useCallback(() => setShowTooltip(false), []);
    if (!localVideoController) {
        return _jsx(_Fragment, {});
    }
    return (_jsx(Tooltip, { showTooltip: showTooltip, message: _jsx(TooltipMessage, { text: "No Video Devices Detected" }), distance: 20, children: _jsx(SelectControlWrapper, { onMouseEnter: show, onMouseOver: show, onMouseLeave: hide, onMouseOut: hide, onFocus: show, onBlur: hide, children: _jsx(Select, { isDisabled: !areLocalCameraDevicesAvailable, isSearchable: false, menuPlacement: "top", onSelect: async (option) => {
                    setCameraSelectValue(option.value);
                    await localVideoController?.restart({
                        deviceId: {
                            exact: option.value,
                        },
                    });
                }, options: localCameraDevices.map(device => ({
                    value: device.deviceId,
                    label: device.label,
                })), components: {
                    IndicatorSeparator: () => null,
                    MenuList: ({ children, ...props }) => (_jsxs(ReactSelectComponents.MenuList, { ...props, children: [_jsx(AppText, { "data-testid": "select-camera", bold: true, style: { padding: '8px 12px' }, children: "Select a Camera" }), children] })),
                    DropdownIndicator: ({ children, ...props }) => {
                        const innerProps = Object.assign({}, props.innerProps, {
                            'data-testid': 'camera-select-dropdown',
                        });
                        return (_jsx(ReactSelectComponents.DropdownIndicator, { ...props, innerProps: innerProps, children: children }));
                    },
                }, styles: {
                    menu: (provided) => ({
                        ...provided,
                        width: '220px',
                        right: '8px',
                        padding: '0px',
                    }),
                    valueContainer: (base) => ({
                        ...base,
                        padding: '0px',
                        width: '0',
                    }),
                    control: (base, { selectProps }) => ({
                        ...base,
                        borderWidth: '0px',
                        boxShadow: 'none',
                        ':focus': {
                            borderWidth: '0px',
                        },
                        background: theme.colors.white,
                        cursor: selectProps.isDisabled ? 'not-allowed' : 'pointer',
                        pointerEvents: selectProps.isDisabled ? 'auto' : null,
                    }),
                    dropdownIndicator: (base, { selectProps }) => ({
                        ...base,
                        color: selectProps.isDisabled
                            ? theme.colors.grey30
                            : localVideoControllerState?.enabled
                                ? theme.colors.blue50
                                : theme.colors.red50Transparent,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 0.5rem top 0.5rem',
                        backgroundSize: '20px 20px',
                        alignItems: 'flex-end',
                        padding: '0 0 .5rem 0',
                        transform: 'rotate(180deg)',
                        height: `${UIConstants.BOTTOM_ROW_ELEMENT_HEIGHT_REM}rem`,
                        ':hover, :active': {
                            color: selectProps.isDisabled
                                ? theme.colors.grey30
                                : localVideoControllerState?.enabled
                                    ? theme.colors.blue50
                                    : theme.colors.red50Transparent,
                            backgroundColor: selectProps.isDisabled
                                ? theme.colors.white
                                : theme.colors.grey10,
                            borderRadius: '.25rem',
                        },
                    }),
                    option: (base) => ({
                        ...base,
                        backgroundColor: 'none',
                        ':hover': {
                            backgroundColor: theme.colors.blue05,
                            cursor: 'pointer',
                        },
                    }),
                }, formatOptionLabel: (option) => (_jsxs(OptionContainer, { "data-testid": `camera-option-${option.label}${option.value === cameraSelectValue ? '-selected' : ''}`, children: [option.value === cameraSelectValue && (_jsx(AppIcon, { name: "DoneIcon", "data-testid": "camera-option-checkmark", color: theme.colors.black, style: { alignSelf: 'center', flexGrow: 1 } })), _jsx(OptionText, { children: option.label })] })) }) }) }));
};
const SelectControlWrapper = styled.div(() => ({
    height: `${UIConstants.BOTTOM_ROW_ELEMENT_HEIGHT_REM}rem`,
}));
const OptionContainer = styled('div')({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
});
const OptionText = styled(AppText)({
    textAlign: 'right',
    width: '100%',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
});
//# sourceMappingURL=local_video_input_select.js.map